'use client';

import { useCallback, useState, useMemo, useEffect } from 'react';
import ProductContainer from '@molecules/ProductContainer/ProductContainer';
import LoadMore from '@molecules/LoadMore/LoadMore';
import packageComponentHelper from '@helpers/packageComponentHelper';
import Text from '@atoms/Text/Text';
import Heading from '@atoms/Heading/Heading';
import ProductBeamSkeleton from '@molecules/ProductBeam/ProductBeam.skeleton';
import useGridTemplateColumns from '@hooks/useGridTemplateColumns';
import FilterAndSort from '@organisms/FilterAndSort/FilterAndSort';
import useProductBannerItems from '@molecules/ProductBeam/useProductBannerItems';
import paths from '@constants/paths';
import SkipToContent from '@molecules/SkipToContent/SkipToContent';
import { useTranslations } from 'next-intl';
import Spinner from '@molecules/Spinner/Spinner';
import useSortOptions from '@hooks/useSortOptions';
import useResponsiveRows from '@hooks/useResponsiveRows';
import usePackageData from '@hooks/usePackageData';
import useMediaQueryKey from '@hooks/useMediaQueryKey';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { usePathname } from 'next/navigation';
import { StyledAlert, StyledHeading, StyledRefContainer } from './ProductBeam.styles';
import useSmartEdit from '@smartedit/hooks/useSmartEdit';

interface Props {
  data: ProductBannerComponentType;
  displayFilters?: boolean;
}

const LOAD_AMOUNT_OF_ROWS = 5;

const ProductBeam = ({ data, displayFilters }: Props) => {
  const pathname = usePathname();

  const { component, packages, isLoading, mutate } = useProductBannerItems(data.uid, displayFilters, data.cartFilter); // without data.cartFilter, CloudFront caching for product beam will not work
  const t = useTranslations('common');
  const responsiveRows = useResponsiveRows(data);
  const { isSmartEditEnabled } = useSmartEdit();

  // Refs and basic state
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [itemsPerRow, setItemsPerRow] = useState(0);
  const [numberOfRows, setNumberOfRows] = useState<number>(1);

  // Custom hooks
  const { sortOptions, onSort } = useSortOptions(component, displayFilters);
  const getCurrentMediaQueryKey = useMediaQueryKey();

  // Computed values
  const isHomePage = pathname === paths.HOME;
  const targetId = `${data.uid}-beam-target`;
  const packageHelper = packageComponentHelper();

  // Package helper
  const gridPackageData = usePackageData(packages, isHomePage);

  const getBeamListName = () => {
    if (data?.isHeroBeam === 'true') {
      return `hero | ${component?.title}`;
    } else if (displayFilters) {
      return data?.uid;
    } else if (isHomePage) {
      return `start_page | ${component?.title}`;
    }
    return component?.title;
  };

  // Calculate product limit based on rows and packages
  const productLimit = useMemo(() => {
    if (itemsPerRow === 0) return 0;

    let reduceLimitBy = 0;

    gridPackageData?.forEach((packageComp: any) => {
      if (parseInt(packageComp.displayRow, 10) <= numberOfRows) {
        reduceLimitBy += packageHelper.getSpanSize(packageComp)[getCurrentMediaQueryKey()] - 1;
      }
    });

    return numberOfRows * itemsPerRow - reduceLimitBy;
  }, [numberOfRows, itemsPerRow, gridPackageData, packageHelper, getCurrentMediaQueryKey]);

  // Process items with packages positioned correctly
  const items = useMemo(() => {
    if (itemsPerRow === 0 || !component?.paginationData?.items) return [];

    return packageHelper.positionPackagesInList(
      component.paginationData.items,
      gridPackageData,
      getCurrentMediaQueryKey(),
      itemsPerRow
    );
  }, [itemsPerRow, component, gridPackageData, packageHelper, getCurrentMediaQueryKey]);

  const handleRef = useCallback((node: HTMLDivElement) => {
    setRef(node);
  }, []);

  // Effects
  useGridTemplateColumns(setItemsPerRow, ref, isLoading);

  useEffect(() => {
    setNumberOfRows(responsiveRows);
  }, [responsiveRows]);

  // In smartedit, mutate when data changes to ensure the latest content is shown, as it wont trigger a re-render otherwise
  useEffect(() => {
    if (isSmartEditEnabled) {
      mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Handle loading more rows
  const onLoadMore = useCallback(() => {
    setNumberOfRows((prevRows) => prevRows + LOAD_AMOUNT_OF_ROWS);
  }, []);

  // State to determine if there are more pages to load
  const hasNextPage = useMemo(() => {
    return productLimit < items.length;
  }, [productLimit, items.length]);

  // Infinite scroll hook
  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore,
    disabled: data?.hasInfiniteScroll !== 'true',
    rootMargin: '0px 0px 400px 0px',
  });

  // Early returns for special states
  if (component?.componentNotFound) {
    return null;
  }

  if (component && !component.paginationData?.items?.length) {
    return null;
  }

  if (component?.emptyProductListTitle && component?.emptyProductListBody) {
    return (
      <StyledAlert id="emptyListMsg" theme="permanent" hasCloseButton={false}>
        <Heading variant="h2">{component.emptyProductListTitle}</Heading>
        <Text>{component.emptyProductListBody}</Text>
      </StyledAlert>
    );
  }

  // Main render
  return (
    <>
      {(data.title || (component && component.paginationData)) && (
        <StyledHeading variant="h2" size="small">
          {data.title || component?.title}
        </StyledHeading>
      )}

      {displayFilters && (
        <FilterAndSort
          filterOptions={component?.facets}
          selectedFilters={component?.breadcrumbs}
          sortOptions={sortOptions}
          onSort={onSort}
          totalResults={component?.paginationData?.items?.length}
          gtmTrackOnFilterOpened={false}
          forceHideRelevanceTooltip={true}
        />
      )}

      {component?.paginationData && (
        <StyledRefContainer ref={handleRef} id={targetId}>
          {isHomePage && (
            <SkipToContent
              buttonText={t('navigation.skipProductBeam')}
              targetElementSelector={`#${targetId}`}
              focusLast
            />
          )}
          <ProductContainer
            title={component?.title as string}
            products={items}
            category={getBeamListName()}
            productType="online"
            numberOfGridCols={itemsPerRow}
            limit={productLimit}
          />
          {data?.hasInfiniteScroll === 'true' && hasNextPage && <Spinner size={32} ref={infiniteRef} />}
          {data?.hasInfiniteScroll !== 'true' && !isLoading && (
            <LoadMore
              pageSize={LOAD_AMOUNT_OF_ROWS}
              total={items.length}
              limit={productLimit}
              onClick={onLoadMore}
              loading={isLoading}
              showProgress={true}
            />
          )}
        </StyledRefContainer>
      )}
      {!component && <ProductBeamSkeleton hasTitle={!!data.title} rows={numberOfRows} />}
    </>
  );
};

export default ProductBeam;
